/**
 * Image Cache Service
 * Service for caching images using the Cache API
 * Provides persistent local storage for experience card images
 */

const CACHE_NAME = 'nexgo-images-v1';
const CACHE_EXPIRY_DAYS = 30; // Images expire after 30 days

// Global rate limiter to prevent ERR_INSUFFICIENT_RESOURCES
const MAX_CONCURRENT_REQUESTS = 6; // Limit concurrent fetch requests globally
let activeRequests = 0;
const requestQueue: Array<{
  url: string;
  resolve: (value: boolean) => void;
}> = [];

interface CachedImageMetadata {
  url: string;
  timestamp: number;
  expiresAt: number;
}

/**
 * Process queued image cache requests with rate limiting
 */
async function processRequestQueue() {
  while (requestQueue.length > 0 && activeRequests < MAX_CONCURRENT_REQUESTS) {
    const { url, resolve } = requestQueue.shift()!;
    activeRequests++;

    try {
      const result = await cacheImageInternal(url);
      resolve(result);
    } catch (error) {
      // cacheImageInternal should never throw, but handle just in case
      // Always resolve with false instead of rejecting
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[ImageCacheService] Unexpected error in rate limiter:',
          error
        );
      }
      resolve(false);
    } finally {
      activeRequests--;
      // Process next request in queue
      processRequestQueue();
    }
  }
}

/**
 * Get the cache instance
 */
async function getCache(): Promise<Cache | null> {
  if (typeof window === 'undefined' || !('caches' in window)) {
    console.warn('[ImageCacheService] Cache API not available');
    return null;
  }

  try {
    return await caches.open(CACHE_NAME);
  } catch (error) {
    console.error('[ImageCacheService] Failed to open cache:', error);
    return null;
  }
}

/**
 * Check if an image is cached and not expired
 */
export async function isImageCached(url: string): Promise<boolean> {
  if (!url) return false;

  const cache = await getCache();
  if (!cache) return false;

  try {
    const cachedResponse = await cache.match(url);
    if (!cachedResponse) return false;

    // Check if response has metadata header
    const metadataHeader = cachedResponse.headers.get('X-Cache-Metadata');
    if (metadataHeader) {
      try {
        const metadata: CachedImageMetadata = JSON.parse(metadataHeader);
        const now = Date.now();
        if (now > metadata.expiresAt) {
          // Expired, remove from cache
          await cache.delete(url);
          return false;
        }
      } catch {
        // Invalid metadata, treat as not cached
        return false;
      }
    }

    return true;
  } catch (error) {
    // Silently fail - cache check errors are non-critical
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('[ImageCacheService] Error checking cache:', error);
    }
    return false;
  }
}

/**
 * Get cached image as blob URL
 * Returns null if not cached or expired
 */
export async function getCachedImage(url: string): Promise<string | null> {
  if (!url) return null;

  const cache = await getCache();
  if (!cache) return null;

  try {
    const cachedResponse = await cache.match(url);
    if (!cachedResponse) return null;

    // Check expiration
    const metadataHeader = cachedResponse.headers.get('X-Cache-Metadata');
    if (metadataHeader) {
      try {
        const metadata: CachedImageMetadata = JSON.parse(metadataHeader);
        const now = Date.now();
        if (now > metadata.expiresAt) {
          await cache.delete(url);
          return null;
        }
      } catch {
        // Invalid metadata, treat as not cached
        return null;
      }
    }

    const blob = await cachedResponse.blob();

    // Validate blob before creating URL
    if (!blob || blob.size === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[ImageCacheService] Invalid blob (empty or null) for cached image, removing from cache'
        );
      }
      // Remove invalid cache entry
      await cache.delete(url);
      return null;
    }

    // Validate blob type is an image (if type is available)
    if (blob.type && !blob.type.startsWith('image/')) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `[ImageCacheService] Blob is not an image type (${blob.type}), removing from cache`
        );
      }
      await cache.delete(url);
      return null;
    }

    try {
      // Create blob URL - if this fails, the blob is likely corrupted
      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    } catch (blobError) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[ImageCacheService] Error creating blob URL (blob may be corrupted):',
          blobError
        );
      }
      // Remove invalid cache entry
      await cache.delete(url);
      return null;
    }
  } catch (error) {
    // Silently fail - cache retrieval errors are non-critical
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('[ImageCacheService] Error getting cached image:', error);
    }
    // Try to remove potentially corrupted cache entry
    try {
      const cache = await getCache();
      if (cache) {
        await cache.delete(url);
      }
    } catch {
      // Ignore cleanup errors
    }
    return null;
  }
}

/**
 * Clear cache entry that might have CORS issues
 */
async function clearCorsFailedCache(url: string): Promise<void> {
  try {
    const cache = await getCache();
    if (cache) {
      await cache.delete(url);
    }
  } catch {
    // Ignore cleanup errors
  }
}

/**
 * Internal function to cache an image (without rate limiting)
 * This is called by the rate-limited cacheImage function
 */
async function cacheImageInternal(url: string): Promise<boolean> {
  // Wrap everything in try-catch to ensure no errors escape
  try {
    if (!url) return false;

    // Check if already cached (wrap in try-catch for safety)
    try {
      if (await isImageCached(url)) {
        return true;
      }
    } catch {
      // If cache check fails, continue to try caching
    }

    const cache = await getCache();
    if (!cache) return false;

    // Before fetching, check and clear potentially corrupted cache entries
    // This helps when browser cache has stored a failed CORS response
    try {
      const cachedResponse = await cache.match(url);
      if (cachedResponse) {
        // Check if cached response might be corrupted (no valid content-type or empty)
        const contentType = cachedResponse.headers.get('content-type');
        const hasValidContentType =
          contentType && contentType.startsWith('image/');

        // If no valid content type, it might be a corrupted CORS failure
        if (!hasValidContentType) {
          await cache.delete(url);
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              `[ImageCacheService] Removed potentially corrupted cache entry: ${url.substring(0, 50)}...`
            );
          }
        }
      }
    } catch {
      // Ignore cache check errors
    }

    // Fetch the image with timeout and better error handling
    let response: Response;

    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout (increased)

      // Fetch with explicit referrer policy to avoid cancellation issues
      // Using 'no-referrer' prevents referrer policy from blocking the request
      // Keep-alive helps prevent request cancellations
      // cache: 'no-cache' prevents using stale CORS-failed responses from browser cache
      response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer', // Avoid referrer policy cancellation
        keepalive: true, // Keep connection alive to prevent cancellations
        cache: 'no-cache', // Force fresh request, don't use cached CORS failures
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
    } catch (fetchError) {
      // Handle all fetch errors gracefully - never throw
      const errorMessage =
        fetchError instanceof Error ? fetchError.message : String(fetchError);

      // Detect CORS errors specifically
      const isCorsError =
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('CORS') ||
        errorMessage.includes('Access-Control') ||
        errorMessage.includes('NetworkError') ||
        (fetchError instanceof TypeError &&
          errorMessage.includes('network') &&
          !errorMessage.includes('aborted'));

      if (isCorsError) {
        // Clear any cached entry that might have CORS issues
        await clearCorsFailedCache(url);

        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[ImageCacheService] CORS error detected (likely extension or cache issue). Cleared cache entry. Try disabling extensions or clearing browser cache: ${url.substring(0, 50)}...`
          );
        }
        return false;
      }

      // Check for ERR_INSUFFICIENT_RESOURCES specifically
      if (
        errorMessage.includes('ERR_INSUFFICIENT_RESOURCES') ||
        errorMessage.includes('insufficient resources') ||
        (errorMessage.includes('Failed to fetch') &&
          errorMessage.includes('net::ERR_INSUFFICIENT_RESOURCES'))
      ) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[ImageCacheService] Insufficient resources for image (will retry later): ${url.substring(0, 50)}...`
          );
        }
        return false; // Fail gracefully, allow retry when resources available
      }

      // Check for canceled requests (referrer policy, CORS, or abort)
      if (
        errorMessage.includes('canceled') ||
        errorMessage.includes('aborted') ||
        errorMessage.includes('AbortError') ||
        (fetchError instanceof DOMException && fetchError.name === 'AbortError')
      ) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[ImageCacheService] Request canceled for image (timeout or referrer policy): ${url.substring(0, 50)}...`
          );
        }
        return false;
      }

      if (fetchError instanceof TypeError) {
        if (
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('aborted')
        ) {
          // CORS error, network error, or timeout
          const errorType = errorMessage.includes('aborted')
            ? 'timeout'
            : 'network/CORS';
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              `[ImageCacheService] ${errorType} error for image (non-critical): ${url.substring(0, 50)}...`
            );
          }
          return false;
        }
      }
      // For any other error, also return false instead of throwing
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[ImageCacheService] Fetch error (non-critical):',
          fetchError
        );
      }
      return false;
    }

    if (!response.ok) {
      console.warn(
        `[ImageCacheService] Failed to fetch image: ${response.status} ${response.statusText}`
      );
      return false;
    }

    // Clone the response as it can only be consumed once
    const responseToCache = response.clone();

    // Create metadata
    const now = Date.now();
    const expiresAt = now + CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    const metadata: CachedImageMetadata = {
      url,
      timestamp: now,
      expiresAt,
    };

    // Create new response with metadata header
    const headers = new Headers(responseToCache.headers);
    headers.set('X-Cache-Metadata', JSON.stringify(metadata));

    const modifiedResponse = new Response(responseToCache.body, {
      status: responseToCache.status,
      statusText: responseToCache.statusText,
      headers,
    });

    // Store in cache
    await cache.put(url, modifiedResponse);
    return true;
  } catch (error) {
    // Handle different types of errors gracefully
    const errorMessage = error instanceof Error ? error.message : String(error);

    // Check for ERR_INSUFFICIENT_RESOURCES in outer catch
    if (
      errorMessage.includes('ERR_INSUFFICIENT_RESOURCES') ||
      errorMessage.includes('insufficient resources')
    ) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `[ImageCacheService] Insufficient resources for image (will retry later): ${url.substring(0, 50)}...`
        );
      }
      return false;
    }

    if (error instanceof TypeError) {
      // Network errors, CORS errors, etc.
      if (errorMessage.includes('Failed to fetch')) {
        console.warn(
          `[ImageCacheService] Network/CORS error for image (this is normal for some images): ${url.substring(0, 50)}...`
        );
        return false;
      }
    }

    if (error instanceof DOMException) {
      if (error.name === 'QuotaExceededError') {
        console.warn(
          '[ImageCacheService] Cache quota exceeded, clearing old entries'
        );
        await clearExpiredImages();
        // Try once more after cleanup
        try {
          const retryCache = await getCache();
          if (!retryCache) return false;

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);

          const response = await fetch(url, {
            mode: 'cors',
            credentials: 'omit',
            referrerPolicy: 'no-referrer', // Avoid referrer policy issues
            keepalive: true, // Keep connection alive to prevent cancellations
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            const responseToCache = response.clone();
            const now = Date.now();
            const expiresAt = now + CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
            const metadata: CachedImageMetadata = {
              url,
              timestamp: now,
              expiresAt,
            };
            const headers = new Headers(responseToCache.headers);
            headers.set('X-Cache-Metadata', JSON.stringify(metadata));
            const modifiedResponse = new Response(responseToCache.body, {
              status: responseToCache.status,
              statusText: responseToCache.statusText,
              headers,
            });
            await retryCache.put(url, modifiedResponse);
            return true;
          }
        } catch (retryError) {
          // Silently fail retry - don't log as error since this is expected
          if (
            retryError instanceof TypeError &&
            retryError.message.includes('Failed to fetch')
          ) {
            return false;
          }
          console.warn('[ImageCacheService] Retry failed:', retryError);
        }
        return false;
      }
    }

    // Log other errors but don't crash
    if (error instanceof Error) {
      // Only log as warning, not error, since cache failures are non-critical
      console.warn(
        `[ImageCacheService] Error caching image (non-critical): ${error.message}`
      );
    } else {
      console.warn('[ImageCacheService] Unknown error caching image:', error);
    }
    return false;
  }
}

/**
 * Cache an image from a URL (rate-limited)
 * Fetches the image and stores it in the cache
 * Never throws - always returns false on error
 * Uses global rate limiting to prevent ERR_INSUFFICIENT_RESOURCES
 */
export async function cacheImage(url: string): Promise<boolean> {
  if (!url) return false;

  // If already cached, return immediately without queuing
  try {
    if (await isImageCached(url)) {
      return true;
    }
  } catch {
    // If cache check fails, continue to try caching
  }

  // Use rate limiter to prevent too many concurrent requests
  return new Promise<boolean>(resolve => {
    requestQueue.push({
      url,
      resolve,
    });
    processRequestQueue();
  });
}

/**
 * Preload and cache multiple images
 * Returns array of booleans indicating success for each URL
 * Never throws - always returns array of booleans
 * Uses rate limiting to prevent ERR_INSUFFICIENT_RESOURCES
 */
export async function cacheImages(urls: string[]): Promise<boolean[]> {
  // Wrap in try-catch to ensure we never throw
  try {
    if (!urls || urls.length === 0) {
      return [];
    }

    // Map each URL to a safe cacheImage call that never throws
    const cachePromises = urls.map(async url => {
      try {
        return await cacheImage(url);
      } catch {
        // Even if cacheImage somehow throws, catch it here
        return false;
      }
    });

    const results = await Promise.allSettled(cachePromises);

    return results.map(result => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        // This should never happen since cacheImage never throws, but handle it anyway
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[ImageCacheService] Unexpected error in cacheImages:',
            result.reason
          );
        }
        return false;
      }
    });
  } catch (error) {
    // Final safety net - should never happen
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '[ImageCacheService] Unexpected error in cacheImages:',
        error
      );
    }
    // Return array of false values
    return urls.map(() => false);
  }
}

/**
 * Clear expired images from cache
 */
export async function clearExpiredImages(): Promise<number> {
  const cache = await getCache();
  if (!cache) return 0;

  let clearedCount = 0;
  const now = Date.now();

  try {
    const keys = await cache.keys();
    for (const request of keys) {
      try {
        const response = await cache.match(request);
        if (response) {
          const metadataHeader = response.headers.get('X-Cache-Metadata');
          if (metadataHeader) {
            try {
              const metadata: CachedImageMetadata = JSON.parse(metadataHeader);
              if (now > metadata.expiresAt) {
                await cache.delete(request);
                clearedCount++;
              }
            } catch {
              // Invalid metadata, remove it
              await cache.delete(request);
              clearedCount++;
            }
          }
        }
      } catch (error) {
        console.error('[ImageCacheService] Error checking cache entry:', error);
      }
    }
  } catch (error) {
    console.error('[ImageCacheService] Error clearing expired images:', error);
  }

  return clearedCount;
}

/**
 * Remove a specific image from cache
 * Useful when a cached image is found to be invalid
 */
export async function removeCachedImage(url: string): Promise<boolean> {
  if (!url) return false;

  const cache = await getCache();
  if (!cache) return false;

  try {
    return await cache.delete(url);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[ImageCacheService] Error removing cached image:', error);
    }
    return false;
  }
}

/**
 * Clear all cached images
 */
export async function clearCache(): Promise<boolean> {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return false;
  }

  try {
    const deleted = await caches.delete(CACHE_NAME);
    return deleted;
  } catch (error) {
    console.error('[ImageCacheService] Error clearing cache:', error);
    return false;
  }
}

/**
 * Get approximate cache size (number of entries)
 */
export async function getCacheSize(): Promise<number> {
  const cache = await getCache();
  if (!cache) return 0;

  try {
    const keys = await cache.keys();
    return keys.length;
  } catch (error) {
    console.error('[ImageCacheService] Error getting cache size:', error);
    return 0;
  }
}

/**
 * Initialize cache cleanup on service worker or page load
 * Cleans up expired images periodically
 */
export async function initializeCacheCleanup(): Promise<void> {
  if (typeof window === 'undefined') return;

  // Clean up expired images on load
  await clearExpiredImages();

  // Set up periodic cleanup (every 24 hours)
  if (typeof window !== 'undefined') {
    setInterval(
      async () => {
        await clearExpiredImages();
      },
      24 * 60 * 60 * 1000
    );
  }
}

// Initialize cleanup on module load
if (typeof window !== 'undefined') {
  initializeCacheCleanup().catch(console.error);
}
